import type { TDataUserAction } from '~/stores/userAction';

import { STORAGE_USER_ACTION_KEY } from '~/constants/local-storage';
import { useCallback, useEffect, useMemo } from 'react';
import { EPersistKey } from '~/types/persist';
import { getShopifyConfig } from '~/config';
import { isBrowser } from '~/utils';

import useUserActionStore from '~/stores/userAction';

export interface IUserActionHandlersProps {
  /**
   * The key to local storage.
   * @default EPersistKey.GENERAL
   */
  persistKey?: EPersistKey;
}
export const useUserActionHandlers = (props?: IUserActionHandlersProps) => {
  const { persistKey = EPersistKey.GENERAL } = props ?? {};

  const storeSetUserAction = useUserActionStore((state) => state.setUserAction);
  const storeGetUserAction = useUserActionStore((state) => state.getUserAction);
  const userAction = useUserActionStore((state) => state.data?.[persistKey]);

  const storageKey = useMemo(() => {
    const shopifyDomain = getShopifyConfig()?.shopifyDomain;
    return shopifyDomain ? `${STORAGE_USER_ACTION_KEY}${shopifyDomain}${persistKey}` : '';
  }, [persistKey]);

  const initUserActionStore = useCallback(() => {
    const cachedUserAction = window.localStorage.getItem(storageKey);
    storeSetUserAction(persistKey, JSON.parse(cachedUserAction || '{}'));
  }, [persistKey, storeSetUserAction, storageKey]);

  const setUserActionBy = useCallback(
    (actionType: string, value: string, isDismissedForSession?: boolean) => {
      const userAction = storeGetUserAction(persistKey);
      const updatedUserAction = {
        ...userAction,
        [actionType]: value,
      };
      storeSetUserAction(persistKey, updatedUserAction);
      !isDismissedForSession && setUserActionToLocal(updatedUserAction);
    },

    [persistKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setUserActionsBy = useCallback(
    (actions: TDataUserAction[]) => {
      const updatedUserAction = { ...userAction };
      actions.forEach(({ actionType, value }) => {
        updatedUserAction[actionType] = value;
      });
      storeSetUserAction(persistKey, updatedUserAction);
      setUserActionToLocal(updatedUserAction);
    },
    [persistKey, userAction], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const setUserActionToLocal = useCallback(
    (dataUserAction: TDataUserAction = {}) => {
      window.localStorage.setItem(storageKey, JSON.stringify(dataUserAction));
    },
    [storageKey],
  );

  const checkDismissed = useCallback((actions: TDataUserAction | undefined, actionType: string) => {
    return !actions || actions?.[actionType] === 'true';
  }, []);

  useEffect(() => {
    const shopifyDomain = getShopifyConfig()?.shopifyDomain;
    if (shopifyDomain && isBrowser() && !userAction) {
      initUserActionStore();
    }
  }, [userAction, initUserActionStore]);

  return {
    actions: userAction,
    hasActionTypeInStore: !!userAction,
    setUserActionBy,
    setUserActionsBy,
    checkDismissed,
  };
};
